import React, { useEffect, useState } from 'react';
import Icon from 'src/components/common/Icon';
import { SlotCategory } from './assignmentsHelpers';
import {
    InlineSpinner,
    LiberateBarButton,
    ModalActions,
    ModalBody,
    ModalCard,
    ModalClose,
    ModalOverlay,
} from './VisibilityAssignment.styles';

interface LiberateConfirmModalProps {
    open: boolean;
    slotCount: number;
    /** Categoría de los slots a liberar, para personalizar el título. */
    category: SlotCategory | null;
    loading: boolean;
    onCancel: () => void;
    onConfirm: (reason: string) => void;
}

const CATEGORY_LABEL: Record<SlotCategory, string> = {
    prime: 'Prime',
    destacado: 'Destacado',
};

/**
 * Confirmación para liberar uno o varios slots ocupados. Permite capturar un
 * motivo opcional que se envía como `observations` al servicio de cancelación.
 */
const LiberateConfirmModal = ({
    open,
    slotCount,
    category,
    loading,
    onCancel,
    onConfirm,
}: LiberateConfirmModalProps) => {
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    if (!open) return null;

    const categoryLabel = category ? ` ${CATEGORY_LABEL[category]}` : '';
    const slotsText =
        slotCount > 1
            ? `${slotCount} slots${categoryLabel}`
            : `slot${categoryLabel}`;

    return (
        <ModalOverlay
            data-id="liberate-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget && !loading) onCancel();
            }}
        >
            <ModalCard>
                <ModalClose
                    type="button"
                    data-id="liberate-close"
                    aria-label="Cerrar"
                    onClick={onCancel}
                    disabled={loading}
                >
                    <Icon icon="pcom-icon-close" size={16} />
                </ModalClose>
                <ModalBody>
                    <h4>Liberar {slotsText}</h4>
                    <p>
                        {slotCount > 1
                            ? 'Las propiedades volverán a estado Libre y podrán reasignarse.'
                            : 'La propiedad volverá a estado Libre y podrá reasignarse.'}
                    </p>
                    <label>
                        Motivo (opcional)
                        <textarea
                            data-id="liberate-reason"
                            placeholder="Describe el motivo de la liberación"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </label>
                    <ModalActions>
                        <LiberateBarButton
                            type="button"
                            data-id="liberate-cancel"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            Cancelar
                        </LiberateBarButton>
                        <LiberateBarButton
                            type="button"
                            data-id="liberate-confirm"
                            onClick={() => onConfirm(reason)}
                            disabled={loading}
                        >
                            {loading ? (
                                <InlineSpinner aria-hidden />
                            ) : (
                                <Icon icon="pcom-icon-delete" size={16} />
                            )}
                            Liberar slots
                        </LiberateBarButton>
                    </ModalActions>
                </ModalBody>
            </ModalCard>
        </ModalOverlay>
    );
};

export default LiberateConfirmModal;
